import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { initialAuthLinkError } from '../../lib/recovery-redirect';
import { supabase } from '../../lib/supabase';
import { Button } from '../../shared/ui/Button';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { PageLoader } from '../../shared/ui/PageLoader';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import { AuthLayout } from './AuthLayout';
import s from './ResetPasswordPage.module.css';
import { resetPasswordSchema, type ResetPasswordInput } from './schemas';
import { signOutAndReset } from './sign-out';

type LinkState = { kind: 'checking' } | { kind: 'ready' } | { kind: 'invalid'; message: string };

const GENERIC_LINK_ERROR =
  'This password reset link is invalid or has already been used. Request a new one and open the newest email.';

/**
 * Where an emailed recovery link lands: prove the link, then set the password.
 *
 * The link itself is the proof. Supabase turns `#…&type=recovery` into a real
 * session during client init (see lib/recovery-redirect), so this page never
 * parses tokens of its own — it asks the client whether a session exists and
 * lets `updateUser` do the rest. That also means the link works from a device
 * other than the one that requested it, which a PKCE flow would not.
 */
export function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const [link, setLink] = useState<LinkState>({ kind: 'checking' });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirm: '' },
  });

  useEffect(() => {
    let cancelled = false;

    // getSession() awaits the client's own initialisation, which is what
    // consumes the link — so by the time it answers, a valid link has already
    // become a session and an invalid one never will.
    void supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        setLink((prev) =>
          prev.kind !== 'checking'
            ? prev
            : data.session !== null
              ? { kind: 'ready' }
              : { kind: 'invalid', message: initialAuthLinkError() ?? GENERIC_LINK_ERROR },
        );
      })
      .catch(() => {
        if (cancelled) return;
        setLink((prev) =>
          prev.kind === 'checking' ? { kind: 'invalid', message: GENERIC_LINK_ERROR } : prev,
        );
      });

    // Belt and braces: if the session lands after that answer, take it rather
    // than leaving the user staring at an "expired link" screen they can't act
    // on. A sign-out (session null) is ignored — this page triggers one itself.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled || session === null) return;
      setLink((prev) => (prev.kind === 'ready' ? prev : { kind: 'ready' }));
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (link.kind === 'ready') setFocus('password');
  }, [link.kind, setFocus]);

  async function onSubmit(values: ResetPasswordInput): Promise<void> {
    setSubmitError(null);
    const { error } = await supabase.auth.updateUser({ password: values.password });
    if (error !== null) {
      setSubmitError(error.message);
      return;
    }
    // End the recovery session deliberately. Sending them to /app instead would
    // drop a half-set-up account straight into the profile dialog or the OTP
    // gate mid-reset, and signing in once proves the new password works.
    try {
      await signOutAndReset();
    } catch {
      /* the password did change; a failed sign-out must not hide that */
    }
    navigate('/login', {
      replace: true,
      state: { notice: 'Password updated. Sign in with your new password.' },
    });
  }

  const pitch = (
    <>
      One password, <em>then back to practice.</em>
    </>
  );

  if (link.kind === 'checking') {
    return <PageLoader label="Checking your reset link…" />;
  }

  if (link.kind === 'invalid') {
    return (
      <AuthLayout
        pitchTitle={pitch}
        pitchBody="Reset links are single-use and expire quickly, so an old email in your inbox will not work. Requesting a new one takes seconds."
      >
        <span className={s.eyebrow}>Reset password</span>
        <h1 className={s.title}>This link no longer works</h1>
        <StatusBanner tone="error">{link.message}</StatusBanner>
        <div className={s.actions}>
          <Link to="/forgot-password" className={s.primaryLink}>
            Request a new link
          </Link>
          <p className={s.footer}>
            <Link to="/login">Back to login</Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      pitchTitle={pitch}
      pitchBody="Choose something you have not used here before. You'll sign in once with the new password to confirm it."
    >
      <span className={s.eyebrow}>Reset password</span>
      <h1 className={s.title}>Set a new password</h1>
      <p className={s.subtitle}>
        Your link checked out. Pick a new password — you'll be signed in with it on the next screen.
      </p>

      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        className={s.form}
      >
        <div>
          <Label htmlFor="password">New password</Label>
          <div className={s.field}>
            <Input
              id="password"
              type={revealed ? 'text' : 'password'}
              autoComplete="new-password"
              enterKeyHint="next"
              placeholder="At least 8 characters"
              className={s.secretInput}
              invalid={errors.password !== undefined}
              aria-describedby={errors.password ? 'password-error' : 'password-help'}
              {...register('password')}
            />
            <button
              type="button"
              className={s.reveal}
              onClick={() => {
                setRevealed((on) => !on);
              }}
              aria-pressed={revealed}
              aria-label={revealed ? 'Hide password' : 'Show password'}
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M1.8 10S4.9 4.5 10 4.5 18.2 10 18.2 10 15.1 15.5 10 15.5 1.8 10 1.8 10Z" />
                <circle cx="10" cy="10" r="2.4" />
                {revealed ? <path d="m3.5 3.5 13 13" /> : null}
              </svg>
            </button>
          </div>
          {errors.password !== undefined ? (
            <FormError id="password-error" message={errors.password.message} />
          ) : (
            <p id="password-help" className={s.hint}>
              8 characters or more. A short phrase you'll remember beats a jumble you won't.
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="confirm">Confirm new password</Label>
          <Input
            id="confirm"
            type={revealed ? 'text' : 'password'}
            autoComplete="new-password"
            enterKeyHint="go"
            placeholder="Type it once more"
            invalid={errors.confirm !== undefined}
            aria-describedby={errors.confirm ? 'confirm-error' : undefined}
            {...register('confirm')}
          />
          <FormError id="confirm-error" message={errors.confirm?.message} />
        </div>

        {submitError !== null ? <StatusBanner tone="error">{submitError}</StatusBanner> : null}

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          Update password
        </Button>
      </form>
    </AuthLayout>
  );
}
